import { ArrowLeft, Check, ClipboardList, FileCheck2, Flag, FolderOpen, GitCompareArrows, Hourglass, Minus, Plus, Scale, ShieldCheck } from 'lucide-react'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { buildCanvasProjection } from '../lib/canvasProjection'
import { formatDate } from '../lib/project'
import type { ProjectRow } from '../types'
import { StatusPill } from '../components/StatusPill'
import { HelpTooltip } from '../components/HelpTooltip'
import { VisualModeTabs } from '../components/VisualModeTabs'
import type { CanvasMode } from '../lib/canvasProjection'

type ProcessCanvas2DProps = {
  project: ProjectRow
  scope: 'general' | 'project'
  onBack: () => void
  onDifferences?: () => void
  onModeChange?: (mode: CanvasMode) => void
}

const phaseIcons = [ShieldCheck, ClipboardList, FileCheck2, FolderOpen, Check]

export function ProcessCanvas2D({ project, scope, onBack, onDifferences, onModeChange }: ProcessCanvas2DProps) {
  const projection = useMemo(() => buildCanvasProjection(project, scope), [project, scope])
  const [selectedPhase, setSelectedPhase] = useState(scope === 'general' ? projection.phases[0]?.id ?? '' : projection.currentPhaseId ?? projection.phases[0]?.id ?? '')
  const [zoom, setZoom] = useState(100)
  const phases = projection.phases
  const phase = phases.find((item) => item.id === selectedPhase) ?? phases[0]
  const visiblePhaseSteps = phase?.steps.filter((step) => !step.isSubstep) ?? []
  const branch = projection.branches[0]

  useEffect(() => {
    if (phases.some((item) => item.id === selectedPhase)) return
    setSelectedPhase(scope === 'general' ? phases[0]?.id ?? '' : projection.currentPhaseId ?? phases[0]?.id ?? '')
  }, [phases, projection.currentPhaseId, scope, selectedPhase])

  return (
    <main className="canvas-page">
      <div className="canvas-topbar">
        <button className="back-link" onClick={onBack} type="button"><ArrowLeft size={17} /> {scope === 'general' ? 'Retour à la référence' : 'Retour au projet'}</button>
        <strong className="canvas-context-label">{scope === 'general' ? 'Référence générale' : project.name}</strong>
        {scope === 'project' && onDifferences ? <button className="secondary-button compact" onClick={onDifferences} title="Consulter les changements proposés pour ce projet" type="button"><Scale size={18} /> Différences</button> : null}
        <span className="canvas-situation">{scope === 'general' ? `En vigueur depuis le ${formatDate(project.snapshot.reference.effective_date)}` : `Situation au ${formatDate(project.as_of_date)}`}</span>
      </div>

      {onModeChange ? <VisualModeTabs active="plan-2d" onChange={onModeChange} /> : null}

      <section className="process-canvas" style={{ '--canvas-zoom': zoom / 100 } as React.CSSProperties}>
        <div className="canvas-grid" />
        <div className="process-track" aria-label="Parcours administratif">
          <div className="track-line" />
          <div className="track-start"><Flag size={18} /><span>Début</span></div>
          {phases.map((item, index) => {
            const Icon = phaseIcons[index % phaseIcons.length]
            const tone = item.timing === 'past' ? 'complete' : item.timing
            return (
              <button
                className={`canvas-phase ${tone} ${selectedPhase === item.id ? 'selected' : ''}`}
                style={{ left: `${12 + index * (76 / Math.max(1, phases.length - 1))}%` }}
                onClick={() => setSelectedPhase(item.id)}
                title={`Afficher les étapes de la phase ${item.order} : ${item.name}`}
                key={item.id}
                type="button"
              >
                <span className="phase-label"><strong>{item.order}. {item.name}</strong><small>{item.primaryStepCount} étape{item.primaryStepCount > 1 ? 's' : ''}</small></span>
                <span className="phase-node"><Icon size={28} /></span>
              </button>
            )
          })}
          <div className="track-end"><Flag size={18} /><span>Fin</span></div>

          <div className="selected-phase-card" style={{ left: `${Math.min(68, Math.max(4, 8 + phases.findIndex((item) => item.id === phase?.id) * (70 / Math.max(1, phases.length - 1))))}%` }}>
            <header><span>Phase {phase?.order}</span><h2>{phase?.name}</h2><Hourglass className="phase-hourglass" size={31} aria-label="Repère de l’étape actuellement suivie" /></header>
            <div className="selected-phase-steps">
              {visiblePhaseSteps.map((step) => (
                <div key={step.id} className={scope === 'project' && step.current ? 'active' : ''}>
                  <span className="tiny-node" />
                  <span><strong>{step.name}</strong><small>{scope === 'general' ? step.expectedOutput : step.nextAction || step.status}</small></span>
                  {scope === 'project' && step.status ? <StatusPill status={step.status} /> : null}
                </div>
              ))}
            </div>
          </div>

          {branch ? <div className="derogation-branch">
            <div className="branch-line left" /><div className="branch-line right" />
            <header><GitCompareArrows size={19} /><span><strong>{branch.name}</strong><small>{scope === 'general' ? 'Branche facultative de la référence' : `Branche facultative · ${branch.applicability === 'oui' ? 'active sur ce projet' : branch.applicability === 'non' ? 'non applicable' : 'à confirmer'}`}</small></span></header>
            <div className="branch-steps">{branch.steps.map((step, index) => <Fragment key={step.id}>{index ? <i /> : null}<span><b>D{step.order}</b><small>{step.name}</small></span></Fragment>)}</div>
          </div> : null}
        </div>

        <aside className="canvas-legend">
          <strong>Clés de lecture <HelpTooltip text="La référence décrit le parcours général. Le projet montre la situation réelle du dossier. L’orange signale un point qui reste à confirmer." label="Comment lire les couches du Plan 2D ?" align="left" /></strong>
          <span><i className="legend-line reference" /> Référence</span>
          {scope === 'project' ? <span><i className="legend-line current" /> Projet</span> : null}
          <span><i className="legend-line uncertain" /> À confirmer</span>
        </aside>

        <div className="canvas-controls">
          <button onClick={() => setZoom((value) => Math.max(80, value - 10))} aria-label="Dézoomer" title="Réduire l’échelle du parcours" type="button"><Minus size={18} /></button>
          <span>{zoom}%</span>
          <button onClick={() => setZoom((value) => Math.min(120, value + 10))} aria-label="Zoomer" title="Agrandir l’échelle du parcours" type="button"><Plus size={18} /></button>
        </div>
      </section>
      <p className="canvas-help">Cliquez sur une phase pour afficher ses étapes. {scope === 'general' ? 'Cette vue décrit la référence générale publiée.' : 'Cette vue décrit uniquement le parcours de ce projet.'}</p>
    </main>
  )
}
